class MobileMenuNav extends HTMLElement {
	constructor() {
		super()
		this.onClick = this.onClick.bind(this)
	}

	connectedCallback() {
		this.stage = this.querySelector('[data-nav-stage]')
		this.root = this.querySelector('[data-nav-root]')
		this.panels = this.querySelectorAll('[data-nav-view^="panel-"]')
		this.addEventListener('click', this.onClick)
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.onClick)
	}

	reset() {
		this.closeAllPanels({ animate: false })
		this.classList.remove('is-panel-active')
		if (this.root) {
			this.root.removeAttribute('aria-hidden')
		}
		this.dataset.navState = 'root'
		this.scrollToTop()
	}

	scrollToTop() {
		const scroller = this.closest('.menu-drawer')
		if (scroller) {
			scroller.scrollTop = 0
		}
	}

	onClick(event) {
		const panelTrigger = event.target.closest(
			'[data-nav-l1-trigger], [data-nav-l2-trigger]'
		)
		if (panelTrigger) {
			event.preventDefault()
			this.showPanel(panelTrigger.dataset.navPanel)
			return
		}

		const backButton = event.target.closest('[data-nav-back]')
		if (backButton) {
			event.preventDefault()
			this.hidePanel(backButton.dataset.navParent)
			return
		}

		const closeLink = event.target.closest('[data-nav-close-drawer]')
		if (closeLink) {
			const drawer = this.closest('header-drawer')
			const summary = drawer?.querySelector('summary')
			if (drawer && summary) {
				drawer.closeMenuDrawer(summary)
			}
		}
	}

	showPanel(panelId) {
		const targetPanel = this.querySelector(`[data-nav-view="${panelId}"]`)
		if (!targetPanel) return

		this.panels.forEach((panel) => {
			if (panel === targetPanel) return
			this.deactivatePanel(panel, { animate: false })
		})

		this.classList.add('is-panel-active')
		if (this.root) {
			this.root.setAttribute('aria-hidden', 'true')
		}

		targetPanel.hidden = false
		targetPanel.setAttribute('aria-hidden', 'false')
		this.scrollToTop()

		if (this.prefersReducedMotion()) {
			targetPanel.classList.add('is-active')
		} else {
			requestAnimationFrame(() => {
				targetPanel.classList.add('is-active')
			})
		}

		this.dataset.navState = panelId
	}

	hidePanel(parentId) {
		const activePanel = this.getActivePanel()

		if (!activePanel) {
			this.reset()
			return
		}

		const finish = () => {
			this.deactivatePanel(activePanel, { animate: false })

			if (parentId === 'root') {
				this.classList.remove('is-panel-active')
				if (this.root) {
					this.root.removeAttribute('aria-hidden')
				}
				this.closeAllPanels({ animate: false })
				this.dataset.navState = 'root'
				this.scrollToTop()
				return
			}

			const parentPanel = this.querySelector(`[data-nav-view="${parentId}"]`)
			if (parentPanel) {
				parentPanel.hidden = false
				parentPanel.setAttribute('aria-hidden', 'false')
				parentPanel.classList.add('is-active')
			}

			this.dataset.navState = parentId
			this.scrollToTop()
		}

		if (this.prefersReducedMotion()) {
			finish()
			return
		}

		activePanel.classList.remove('is-active')
		activePanel.classList.add('is-exiting')
		activePanel.addEventListener('transitionend', finish, { once: true })
	}

	getActivePanel() {
		const activePanels = [
			...this.querySelectorAll(
				'.menu-drawer__nav-panel.is-active:not([hidden])'
			),
		]

		return activePanels.length ? activePanels[activePanels.length - 1] : null
	}

	deactivatePanel(panel, { animate = false } = {}) {
		if (!panel) return

		const done = () => {
			panel.hidden = true
			panel.setAttribute('aria-hidden', 'true')
			panel.classList.remove('is-active', 'is-exiting')
		}

		if (!animate || this.prefersReducedMotion()) {
			done()
			return
		}

		panel.classList.remove('is-active')
		panel.classList.add('is-exiting')
		panel.addEventListener('transitionend', done, { once: true })
	}

	closeAllPanels({ animate = false } = {}) {
		this.panels.forEach((panel) => {
			this.deactivatePanel(panel, { animate })
		})
	}

	prefersReducedMotion() {
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches
	}
}

customElements.define('mobile-menu-nav', MobileMenuNav)

const resetMobileMenuNav = (root = document) => {
	root.querySelectorAll('mobile-menu-nav').forEach((nav) => {
		if (typeof nav.reset === 'function') {
			nav.reset()
		}
	})
}

document.addEventListener('shopify:section:load', () => {
	resetMobileMenuNav()
})
